import {
  TextNode,
  NodeKey,
  EditorConfig,
  LexicalNode,
  SerializedTextNode,
} from "lexical";

interface SerializedColoredTextNode extends SerializedTextNode {
  type: "colored-text";
  color: string;
  backgroundColor: string;
  version: number;
}

export class ColoredTextNode extends TextNode {
  __color: string;
  __backgroundColor: string;

  static getType(): string {
    return "colored-text";
  }

  getColor(): string {
    return this.__color;
  }

  getBackgroundColor(): string {
    return this.__backgroundColor;
  }

  static clone(node: ColoredTextNode): ColoredTextNode {
    return new ColoredTextNode(
      node.__text,
      node.__color,
      node.__backgroundColor,
      node.__key,
    );
  }

  constructor(
    text: string,
    color: string,
    backgroundColor: string,
    key?: NodeKey,
  ) {
    super(text, key);
    this.__color = color;
    this.__backgroundColor = backgroundColor;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.style.color = this.__color;
    element.style.backgroundColor = this.__backgroundColor;
    return element;
  }

  updateDOM(
    prevNode: TextNode,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    const isUpdated = super.updateDOM(prevNode, dom, config);
    const prevColoredNode = prevNode as ColoredTextNode;
    if (prevColoredNode?.__color !== this.__color) {
      dom.style.color = this.__color;
    }
    if (prevColoredNode?.__backgroundColor !== this.__backgroundColor) {
      dom.style.backgroundColor = this.__backgroundColor;
    }
    const currentColor = dom.style.color;
    const currentBackgroundColor = dom.style.backgroundColor;
    if (currentColor !== this.__color) {
      this.__color = currentColor;
    }
    if (currentBackgroundColor !== this.__backgroundColor) {
      this.__backgroundColor = currentBackgroundColor;
    }
    return isUpdated;
  }

  exportJSON(): SerializedColoredTextNode {
    return {
      ...super.exportJSON(),
      type: "colored-text",
      color: this.__color,
      backgroundColor: this.__backgroundColor,
      version: 1,
    };
  }

  static importJSON(
    serializedNode: SerializedColoredTextNode,
  ): ColoredTextNode {
    const node = $createColoredTextNode(
      serializedNode.text,
      serializedNode.color,
      serializedNode.backgroundColor,
    );
    node.setMode(serializedNode.mode);
    node.setDetail(serializedNode.detail);
    node.setFormat(serializedNode.format);
    node.setStyle(serializedNode.style);
    return node;
  }

  setColor(color: string): void {
    this.__color = color;
  }

  setBackgroundColor(backgroundColor: string): void {
    this.__backgroundColor = backgroundColor;
  }
}

export function $createColoredTextNode(
  text: string,
  color: string,
  backgroundColor: string,
): ColoredTextNode {
  return new ColoredTextNode(text, color, backgroundColor);
}

export function $isColoredTextNode(
  node: LexicalNode | null | undefined,
): node is ColoredTextNode {
  return node instanceof ColoredTextNode;
}
